import { useCallback, useLayoutEffect, useRef } from 'react';

import type { RefObject } from 'react';

const FITTING_ATTEMPTS = 8;
const FITTING_BUFFER_PX = 1;
const DEFAULT_MIN_FONT_SIZE = 5;
const DEFAULT_MAX_FONT_SIZE = 16;

type UseFittedAnswerLabelOptions = {
	text: string;
	layoutKey?: string;
};

type UseFittedAnswerLabelResult = {
	labelRef: RefObject<HTMLDivElement | null>;
	labelTextRef: RefObject<HTMLSpanElement | null>;
};

const readCssPixelValue = (element: HTMLElement, propertyName: string, fallback: number) => {
	const value = Number.parseFloat(window.getComputedStyle(element).getPropertyValue(propertyName));
	return Number.isFinite(value) ? value : fallback;
};

const useFittedAnswerLabel = ({ text, layoutKey }: UseFittedAnswerLabelOptions): UseFittedAnswerLabelResult => {
	const labelRef = useRef<HTMLDivElement | null>(null);
	const labelTextRef = useRef<HTMLSpanElement | null>(null);

	const fitLabelText = useCallback(() => {
		const labelNode = labelRef.current;
		const textNode = labelTextRef.current;

		if (!labelNode || !textNode || !text) {
			return;
		}

		const availableWidth = labelNode.clientWidth;
		const availableHeight = labelNode.clientHeight;

		if (!availableWidth || !availableHeight) {
			return;
		}

		const minFontSize = readCssPixelValue(
			labelNode,
			'--lane-chooser-answer-label-min-font-size',
			DEFAULT_MIN_FONT_SIZE,
		);
		const maxFontSize = readCssPixelValue(
			labelNode,
			'--lane-chooser-answer-label-max-font-size',
			DEFAULT_MAX_FONT_SIZE,
		);

		const setFontSize = (fontSize: number) => {
			labelNode.style.setProperty('--lane-chooser-answer-label-font-size', `${fontSize.toFixed(2)}px`);
		};
		const fits = () =>
			textNode.scrollWidth <= availableWidth + FITTING_BUFFER_PX &&
			textNode.scrollHeight <= availableHeight + FITTING_BUFFER_PX;

		setFontSize(maxFontSize);

		if (fits()) {
			return;
		}

		let low = minFontSize;
		let high = maxFontSize;

		for (let i = 0; i < FITTING_ATTEMPTS; i += 1) {
			const candidate = (low + high) / 2;
			setFontSize(candidate);

			if (fits()) {
				low = candidate;
			} else {
				high = candidate;
			}
		}

		setFontSize(low);
	}, [text]);

	useLayoutEffect(() => {
		const labelNode = labelRef.current;
		if (!labelNode || !text) return;

		let isMounted = true;
		let frameId: number | null = null;
		const scheduleFit = () => {
			if (!isMounted) {
				return;
			}

			if (frameId != null) {
				cancelAnimationFrame(frameId);
			}

			frameId = requestAnimationFrame(() => {
				frameId = null;
				fitLabelText();
			});
		};

		scheduleFit();

		const resizeObserver = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(scheduleFit) : null;
		resizeObserver?.observe(labelNode);
		window.addEventListener('resize', scheduleFit);
		void document.fonts?.ready?.then(scheduleFit);

		return () => {
			isMounted = false;

			if (frameId != null) {
				cancelAnimationFrame(frameId);
			}

			resizeObserver?.disconnect();
			window.removeEventListener('resize', scheduleFit);
		};
	}, [fitLabelText, layoutKey, text]);

	return { labelRef, labelTextRef };
};

export default useFittedAnswerLabel;
